import BreadCrumbs from 'components/breadcrumbs'
import LoadingV2 from 'components/LoadingV2'
import PMBTabSwitcher from 'components/PMBTabSwitcher'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { ArrowLeft } from 'react-feather'
import { useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { URL_PROJECTS_MENU } from 'utils/pathUrl'
import ProjectDashboard from '../project-dashboard'
import CalculatorSchema from './CalculatorSchema'
import ModalConfirmClose from './ModalConfirmClose'
import ModalConfirmDelete from './ModalConfirmDelete'
import ModalShareProject from './ModalShareProject'
import ProjectDetailTab from './ProjectDetailTab'
import TableDashboard from '../dashboard/TableDashboard'
import {
  useDeleteProjectMutation,
  useFetchProjectByIdQuery,
  useUpdateProjectMenuMutation,
  useSharedProjectDetailQuery,
  useUpdateProjectMenuSharedMutation,
  useListResourcesAccessQuery,
} from './projectsMenuListApiSlice'

const tabs = [
  { key: 'detail', label: 'Detail' },
  { key: 'summary', label: 'Ringkasan' },
  { key: 'transaction', label: 'Transaksi' },
  { key: 'calculator', label: 'Calculator' },
]

const DetailProject = () => {
  const navigate = useNavigate()
  const { id: projectId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const initialTab =
    tabParam === 'transaksi' || tabParam === 'transaction'
      ? 'transaction'
      : tabParam || 'detail'
  const isShared = searchParams.get('shared') === 'true'
  const [activeTab, setActiveTab] = useState(initialTab)
  const { profile } = useSelector((state) => state.dashboard)

  const [isOpenDelete, setIsOpenDelete] = useState(false)
  const [deleteErrMsg, setDeleteErrMsg] = useState('')
  const [isOpenClose, setIsOpenClose] = useState(false)
  const [closeErrMsg, setCloseErrMsg] = useState('')
  const [isOpenShare, setIsOpenShare] = useState(false)

  const {
    data: ownData,
    isLoading: isLoadingOwn,
    isError: isErrorOwn,
    error: errorOwn,
    refetch: refetchOwn,
  } = useFetchProjectByIdQuery({ projectId }, { skip: !projectId || isShared })

  const {
    data: sharedData,
    isLoading: isLoadingShared,
    isError: isErrorShared,
    error: errorShared,
    refetch: refetchShared,
  } = useSharedProjectDetailQuery(
    { id: projectId },
    { skip: !projectId || !isShared },
  )

  const { data: resourceAccessData, refetch: refetchResourceAccess } =
    useListResourcesAccessQuery(
      { resourceId: projectId, resourceType: 'PROJECT' },
      { skip: !projectId },
    )

  const data = isShared ? sharedData : ownData
  const isLoadingProjectById = isShared ? isLoadingShared : isLoadingOwn
  const isErrorProject = isShared ? isErrorShared : isErrorOwn
  const errorProject = isShared ? errorShared : errorOwn
  const refetchProject = isShared ? refetchShared : refetchOwn

  // Check if project not found (500 error or "Project tidak ditemukan" message)
  const isProjectNotFound =
    isErrorProject &&
    (errorProject?.status === 500 ||
      errorProject?.data?.meta?.message
        ?.toLowerCase()
        ?.includes('tidak ditemukan') ||
      errorProject?.data?.message?.toLowerCase()?.includes('tidak ditemukan'))

  // Check user role for shared project
  const getUserRole = () => {
    if (!isShared || !profile?.userId || !resourceAccessData?.participants)
      return null
    const participant = resourceAccessData.participants.find(
      (p) => p.userId === profile.userId,
    )
    return participant?.role || null
  }

  const userRole = getUserRole()
  const isViewer = isShared && userRole === 'VIEWER'
  const canEditProject =
    !isShared || userRole === 'OWNER' || userRole === 'EDITOR'

  const availableTabs = isViewer
    ? tabs.filter(
        (t) =>
          t.key !== 'summary' &&
          t.key !== 'transaction' &&
          t.key !== 'calculator',
      )
    : tabs

  useEffect(() => {
    if (
      isViewer &&
      ['summary', 'transaction', 'calculator'].includes(activeTab)
    ) {
      setActiveTab('detail')
    }
  }, [isViewer, activeTab])

  const [deleteProject, { isLoading: isDeleting }] = useDeleteProjectMutation()
  const [updateProjectMenu, { isLoading: isClosing }] =
    useUpdateProjectMenuMutation()
  const [updateProjectMenuShared, { isLoading: isClosingShared }] =
    useUpdateProjectMenuSharedMutation()

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    const newParams = new URLSearchParams(searchParams)
    newParams.set('tab', tab)
    setSearchParams(newParams, { replace: true })
  }

  const handleBack = () => {
    if (window?.history?.length > 2) {
      navigate(-1)
    } else {
      navigate(isShared ? `${URL_PROJECTS_MENU}?tab=shared` : URL_PROJECTS_MENU)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteProject({ projectId }).unwrap()
      enqueueSnackbar(`Project ${data?.label || ''} berhasil dihapus.`, {
        variant: 'success',
      })
      setIsOpenDelete(false)
      setDeleteErrMsg('')
      navigate(URL_PROJECTS_MENU)
    } catch (error) {
      setDeleteErrMsg(
        error?.data?.meta?.message ||
        error?.data?.message ||
        'Terjadi kesalahan saat menghapus project',
      )
    }
  }

  const handleClose = async () => {
    try {
      const closePayload = {
        ...data,
        status: 'CLOSED',
        participants: data?.participants ? data?.participants : [],
        goals: data?.goals ? data?.goals : [],
      }
      if (isShared) {
        await updateProjectMenuShared({
          projectId,
          payload: closePayload,
        }).unwrap()
      } else {
        await updateProjectMenu({
          projectId,
          payload: closePayload,
        }).unwrap()
      }
      enqueueSnackbar(`Project ${data?.label || ''} berhasil ditutup.`, {
        variant: 'success',
      })
      setIsOpenClose(false)
      setCloseErrMsg('')
      refetchProject()
    } catch (error) {
      setCloseErrMsg(
        error?.data?.meta?.message ||
        error?.data?.message ||
        'Terjadi kesalahan saat menutup project',
      )
    }
  }

  if (isLoadingProjectById) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      <div className="mt-3">
        <BreadCrumbs
          breadCrumbParent="Daftar Projects"
          breadCrumbParentLink={
            isShared ? `${URL_PROJECTS_MENU}?shared=true` : URL_PROJECTS_MENU
          }
          breadCrumbActive={data?.title || 'Detail Project'}
        />
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3 mt-2">
        <div className="d-flex align-items-center">
          <ArrowLeft
            size={20}
            className="me-2"
            style={{ cursor: 'pointer' }}
            onClick={handleBack}
          />
          <h6 className="fw-bold mb-0">Detail Project</h6>
        </div>
      </div>

      {!isShared && (
        <ModalConfirmDelete
          isOpen={isOpenDelete}
          toggle={() => {
            setIsOpenDelete(false)
            setDeleteErrMsg('')
          }}
          projectLabel={data?.label || ''}
          onDelete={handleDelete}
          isLoading={isDeleting}
          errMsg={deleteErrMsg}
        />
      )}

      {canEditProject && (
        <ModalConfirmClose
          isOpen={isOpenClose}
          toggle={() => {
            setIsOpenClose(false)
            setCloseErrMsg('')
          }}
          projectLabel={data?.label || ''}
          onClose={handleClose}
          isLoading={isShared ? isClosingShared : isClosing}
          errMsg={closeErrMsg}
        />
      )}

      {!isShared && (
        <ModalShareProject
          isOpen={isOpenShare}
          toggle={() => setIsOpenShare(false)}
          project={data}
          existingParticipants={
            resourceAccessData?.participants &&
            resourceAccessData.participants.length > 0
              ? resourceAccessData.participants
              : Array.isArray(data?.participants)
              ? data.participants
              : []
          }
          refetch={async () => {
            await Promise.allSettled([
              refetchProject && refetchProject(),
              refetchResourceAccess && refetchResourceAccess(),
            ])
          }}
        />
      )}

      {isProjectNotFound ? (
        <>
          <div className="w-100 px-3">
            <div className="d-flex flex-column align-items-center justify-content-center mt-5 pt-5">
              <h5 className="fw-semibold text-center mb-3">
                Project tidak ditemukan atau anda tidak memiliki akses ke
                project tersebut
              </h5>
              <button
                className="btn btn-primary"
                onClick={() =>
                  navigate(
                    isShared
                      ? `${URL_PROJECTS_MENU}?tab=shared`
                      : URL_PROJECTS_MENU,
                  )
                }
              >
                Kembali ke Daftar Project
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <PMBTabSwitcher
            activeTab={activeTab}
            tabs={availableTabs}
            onTabChange={handleTabChange}
            testId="project-tab"
          />

          {activeTab === 'detail' && (
            <ProjectDetailTab
              projectId={projectId}
              isShared={isShared}
              canEdit={canEditProject}
              onDelete={!isShared ? () => setIsOpenDelete(true) : undefined}
              onClose={canEditProject ? () => setIsOpenClose(true) : undefined}
              onShare={!isShared ? () => setIsOpenShare(true) : undefined}
            />
          )}
          {activeTab === 'summary' && !isViewer && (
            <ProjectDashboard
              embedded
              projectId={projectId}
              isShared={isShared}
            />
          )}
          {activeTab === 'transaction' && !isViewer && (
            <div className="mt-3">
              <TableDashboard
                title={
                  data?.label || data?.title
                    ? `Daftar Transaksi Project ${data?.label || data?.title}`
                    : 'Daftar Transaksi'
                }
                isList
                projectId={projectId}
                hideCreate
                hideArchive
              />
            </div>
          )}
          {activeTab === 'calculator' && !isViewer && (
            <CalculatorSchema data={data} projectId={projectId} />
          )}
        </>
      )}
    </div>
  )
}

export default DetailProject
